import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, CheckCircle, Clock, Trash2, Download, FileSignature, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicUpload = () => {
  const { shareId } = useParams();
  const [step, setStep] = useState('email'); // 'email' or 'upload'
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState(''); // Email that passed verification
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocId, setUploadingDocId] = useState(null);

  const hasFile = (doc) => doc.file_path || (doc.file_paths && doc.file_paths.length > 0);
  const [submitting, setSubmitting] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(null);
  // Fix the missing route decorator for implementation documents delete
  const [replacedDocs, setReplacedDocs] = useState({});

  // Extract display filename from a file path string
  const extractFilename = (fp) => {
    if (!fp) return 'Αρχείο';
    // Handle r2:// paths
    if (fp.startsWith('r2://')) {
      const parts = fp.substring(5).split('/');
      const raw = parts[parts.length - 1];
      // Remove UUID prefix if present (format: uuid_filename.pdf)
      const match = raw.match(/^[a-f0-9-]{36}_(.+)$/i);
      return match ? decodeURIComponent(match[1]) : decodeURIComponent(raw);
    }
    // Handle gridfs: paths
    if (fp.startsWith('gridfs:')) {
      const parts = fp.split(':');
      return parts.length >= 3 ? parts.slice(2).join(':') : 'Αρχείο';
    }
    // Regular path
    const parts = fp.split('/');
    return parts[parts.length - 1];
  };

  const handleDeleteSingleFile = async (documentId, fileIndex) => {
    try {
      await axios.delete(`${API}/documents/${documentId}/file?file_index=${fileIndex}`);
      toast.success('Το αρχείο διαγράφηκε');
      fetchCollaboratorDocuments(verifiedEmail);
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείου');
    }
  };

  useEffect(() => {
    fetchProjectInfo();
  }, [shareId]);

  const fetchProjectInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/project/${shareId}`);
      setProject(response.data.project);
      // Don't load documents until email is verified
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργου');
    }
  };

  const fetchCollaboratorDocuments = async (collaboratorEmail) => {
    try {
      const response = await axios.get(`${API}/public/project/${shareId}/documents/${encodeURIComponent(collaboratorEmail)}`);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (email !== emailConfirm) {
      toast.error('Τα emails δεν ταιριάζουν');
      return;
    }

    try {
      const response = await axios.post(`${API}/public/project/${shareId}/email`, {
        email,
        email_confirm: emailConfirm,
      });
      toast.success('Το email επιβεβαιώθηκε');
      setVerifiedEmail(email);
      // Fetch only documents assigned to this collaborator
      await fetchCollaboratorDocuments(email);
      setStep('upload');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα επιβεβαίωσης email');
    }
  };

  const handleUpload = async (documentId, files, replace = false) => {
    if (!files || files.length === 0) return;

    setUploadingDocId(documentId);
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const replaceParam = replace ? '&replace=true' : '';
      await axios.post(`${API}/public/documents/${documentId}/upload?uploader_email=${encodeURIComponent(verifiedEmail)}${replaceParam}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (replace) {
        setReplacedDocs(prev => ({ ...prev, [documentId]: true }));
      }
      toast.success(replace ? 'Τα αρχεία αντικαταστάθηκαν' : `${files.length} αρχείο(α) προστέθηκαν`);
      fetchCollaboratorDocuments(verifiedEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ανεβάσματος αρχείου');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleDeleteFile = async (documentId) => {
    try {
      await axios.delete(`${API}/documents/${documentId}/file?file_index=-1`);
      toast.success('Όλα τα αρχεία διαγράφηκαν');
      fetchCollaboratorDocuments(verifiedEmail);
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείων');
    }
  };

  // Signature functions for collaborators
  const handleDownloadForSigning = async (doc) => {
    try {
      const response = await axios.get(`${API}/public/documents/${doc.document_id}/signature/download-for-signing`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `για_υπογραφή_${doc.document_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleUploadSigned = async (docId, file) => {
    if (!file) return;
    setSignatureUploading(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/public/documents/${docId}/signature/upload-signed`, formData);
      toast.success('Το υπογεγραμμένο έγγραφο ανέβηκε!');
      fetchCollaboratorDocuments(verifiedEmail);
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος');
    } finally {
      setSignatureUploading(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/project/${shareId}/submit`, null, {
        params: { client_email: verifiedEmail }
      });
      toast.success('Η υποβολή ολοκληρώθηκε επιτυχώς! Ο σύμβουλός σας θα ενημερωθεί.');
      
      // Refresh to show updated status
      await fetchProjectInfo();
    } catch (error) {
      toast.error('Σφάλμα υποβολής');
    } finally {
      setSubmitting(false);
    }
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="public-upload-title">
                {project.project_title}
              </h1>
              <p className="text-slate-600">Πελάτης: {project.client_name}</p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="email-form">
              <div className="space-y-2">
                <Label htmlFor="email">Παρακαλώ εισάγετε το email σας</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-confirm">Επιβεβαίωση Email</Label>
                <Input
                  id="email-confirm"
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  required
                  data-testid="email-confirm-input"
                />
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="email-submit-button">
                Συνέχεια →
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" data-testid="upload-heading">
            {project.project_title}
          </h1>
          <p className="text-slate-600">Πελάτης: {project.client_name}</p>
          {verifiedEmail && (
            <p className="text-sm text-slate-500 mt-1">Συνδεδεμένος ως: {verifiedEmail}</p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6" data-testid="documents-list-heading">
            Τα Δικαιολογητικά σας
          </h2>

          {documents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Δεν σας έχουν ανατεθεί δικαιολογητικά ακόμα.</p>
              <p className="text-sm mt-2">Επικοινωνήστε με τον σύμβουλό σας.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="documents-list">
              {documents.map((doc) => (
                <div
                  key={doc.document_id}
                  className={`border rounded-md p-4 transition-colors ${
                    doc.review_status === 'rejected' 
                      ? 'border-rose-300 bg-rose-50' 
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`public-document-${doc.document_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className={`font-medium mb-1 ${doc.review_status === 'rejected' ? 'text-rose-900' : 'text-slate-900'}`}>
                        {doc.document_name}
                      </h3>
                      
                      {/* Status badges */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {doc.review_status === 'rejected' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-100 text-rose-700 text-xs font-medium">
                            ❌ Λάθος - Απαιτείται διόρθωση
                          </span>
                        ) : doc.review_status === 'approved' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Εγκρίθηκε
                          </span>
                        ) : hasFile(doc) ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ανέβηκε - Αναμονή ελέγχου
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                            <Clock className="h-3 w-3 mr-1" />
                            Εκκρεμεί
                          </span>
                        )}
                      </div>
                      
                      {/* Show rejection comment */}
                      {doc.review_status === 'rejected' && doc.review_comment && (
                        <div className="mt-2 p-2 bg-rose-100 rounded-md">
                          <p className="text-sm text-rose-800">
                            <strong>Πρόβλημα:</strong> {doc.review_comment}
                          </p>
                        </div>
                      )}

                      {/* Uploaded files list */}
                      {hasFile(doc) && doc.file_paths && doc.file_paths.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <p className="text-xs font-medium text-slate-500 mb-1">Ανεβασμένα αρχεία ({doc.file_paths.length}):</p>
                          {doc.file_paths.map((fp, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
                              <span className="flex-1 truncate text-slate-700" title={extractFilename(fp)}>{extractFilename(fp)}</span>
                              <button
                                onClick={() => window.open(`${API}/documents/${doc.document_id}/download?file_index=${idx}`, '_blank')}
                                className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                title="Λήψη"
                                data-testid={`download-file-${doc.document_id}-${idx}`}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteSingleFile(doc.document_id, idx)}
                                className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                                title="Διαγραφή"
                                data-testid={`delete-file-${doc.document_id}-${idx}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Signature section for collaborators */}
                      {doc.signature_required && doc.signature_file_path && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <div className="flex items-center gap-2 mb-2">
                            <FileSignature className="h-4 w-4 text-amber-600" />
                            <span className="font-medium text-amber-800 text-sm">Απαιτείται υπογραφή</span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Download file for signing */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadForSigning(doc)}
                              className="text-amber-700 border-amber-300 hover:bg-amber-100"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Λήψη για υπογραφή
                            </Button>
                            
                            {/* Upload signed file */}
                            {doc.signature_status !== 'signed' ? (
                              <>
                                <input
                                  type="file"
                                  id={`signed-file-${doc.document_id}`}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.tiff"
                                  className="hidden"
                                  onChange={(e) => handleUploadSigned(doc.document_id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => document.getElementById(`signed-file-${doc.document_id}`).click()}
                                  disabled={signatureUploading === doc.document_id}
                                  className="bg-amber-600 hover:bg-amber-700"
                                >
                                  <Upload className="h-3 w-3 mr-1" />
                                  {signatureUploading === doc.document_id ? 'Ανέβασμα...' : 'Ανέβασμα υπογεγραμμένου'}
                                </Button>
                              </>
                            ) : (
                              <span className="text-sm text-emerald-600 flex items-center gap-1">
                                <CheckCircle className="h-4 w-4" />
                                Υπογεγραμμένο - Αναμονή ελέγχου
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 ml-4">
                      <input
                        type="file"
                        id={`public-file-${doc.document_id}`}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          // First upload after replace or on empty doc: just append
                          handleUpload(doc.document_id, e.target.files, false);
                        }}
                      />
                      
                      {doc.review_status === 'rejected' ? (
                        <Button
                          onClick={() => {
                            // For rejected docs, clicking triggers a replace upload
                            const input = document.getElementById(`public-file-replace-${doc.document_id}`);
                            if (input) input.click();
                          }}
                          disabled={uploadingDocId === doc.document_id}
                          className="bg-rose-600 hover:bg-rose-700"
                          data-testid={`public-upload-button-${doc.document_id}`}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingDocId === doc.document_id ? 'Ανέβασμα...' : 'Διόρθωση'}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => document.getElementById(`public-file-${doc.document_id}`).click()}
                          disabled={uploadingDocId === doc.document_id}
                          className={hasFile(doc) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}
                          data-testid={`public-upload-button-${doc.document_id}`}
                        >
                          {hasFile(doc) ? <Plus className="h-4 w-4 mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                          {uploadingDocId === doc.document_id 
                            ? 'Ανέβασμα...' 
                            : hasFile(doc)
                              ? 'Προσθήκη επιπλέον εγγράφων'
                              : 'Προσθήκη εγγράφου'}
                        </Button>
                      )}
                      
                      {/* Hidden input for replace on rejected docs */}
                      <input
                        type="file"
                        id={`public-file-replace-${doc.document_id}`}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        multiple
                        className="hidden"
                        onChange={(e) => handleUpload(doc.document_id, e.target.files, true)}
                      />
                      
                      {hasFile(doc) && (
                        <Button
                          onClick={() => handleDeleteFile(doc.document_id)}
                          variant="outline"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-all-files-${doc.document_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Διαγραφή όλων
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
              data-testid="submit-documents-button"
            >
              {submitting ? 'Υποβολή...' : 'Υποβολή'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicUpload;
